import $ from "jquery";
import { IRealtimeTransport } from "./ITransport";
import { ConnectionState } from "../types/events";

// Ensure jQuery is available globally for SignalR 2.x
if (typeof window !== "undefined") {
    window.jQuery = $;
    window.$ = $;
}
require("../lib/jquery.signalR.min.js");

/**
 * SignalR Transport Implementation (ASP.NET SignalR 2.4.x)
 * Connects directly to the ASP.NET backend RealtimeHub.
 */
export class SignalRTransport extends IRealtimeTransport {
    constructor() {
        super();
        this.connection = null;
        this.hubProxy = null;
        this.connectPromise = null;
        this.state = ConnectionState.DISCONNECTED;
        this.stateChangeListeners = new Set();
        this.pendingSubscriptions = new Map();
        this.registeredHubEvents = new Set();
    }

    /**
     * Build and start the SignalR Hub Connection
     * @param {Object} config - { url, qs }
     */
    connect(config = {}) {
        const isConnected = this.connection && this.connection.state === ($.signalR?.connectionState?.connected ?? 1);
        if (isConnected) {
            return Promise.resolve();
        }

        if (this.connectPromise) {
            return this.connectPromise;
        }

        const rawBase = config.url || process.env.REACT_APP_SIGNALR_URL || process.env.REACT_APP_SITE_URL || "https://alarabeyya.com";
        const cleanBase = rawBase.replace(/\/+$/, "").replace(/\/api$/, "");

        this.connectPromise = new Promise((resolve) => {
            try {
                this.setState(ConnectionState.CONNECTING);

                // Initialize ASP.NET SignalR Hub Connection
                this.connection = $.hubConnection(cleanBase, { useDefaultPath: false });
                this.connection.url = `${cleanBase}/signalr`;

                if (config.qs) {
                    this.connection.qs = config.qs;
                }

                this.connection.logging = process.env.NODE_ENV === "development";

                // Create proxy for realtimeHub
                this.hubProxy = this.connection.createHubProxy("realtimeHub");

                this.setupLifecycleHandlers(resolve);
                this.bindPendingSubscriptions();

                // Start connection with transport fallback
                this.connection.start({
                    transport: ["webSockets", "serverSentEvents", "longPolling"],
                    jsonp: false
                })
                .done(() => {
                    this.setState(ConnectionState.CONNECTED);
                    console.log("[SignalRTransport] Connected successfully to ASP.NET SignalR. ConnectionId:", this.connection.id);
                    this.connectPromise = null;
                    resolve();
                })
                .fail((error) => {
                    this.setState(ConnectionState.DISCONNECTED);
                    console.error("[SignalRTransport] Connection failed:", error);
                    this.connectPromise = null;
                    resolve();
                });
            } catch (err) {
                this.setState(ConnectionState.DISCONNECTED);
                console.error("[SignalRTransport] Initialization error:", err);
                this.connectPromise = null;
                resolve();
            }
        });

        return this.connectPromise;
    }

    setupLifecycleHandlers(resolveConnectPromise) {
        if (!this.connection) return;

        this.connection.stateChanged((change) => {
            const states = $.signalR.connectionState;
            switch (change.newState) {
                case states.connecting:
                    this.setState(ConnectionState.CONNECTING);
                    break;
                case states.connected:
                    this.setState(ConnectionState.CONNECTED);
                    break;
                case states.reconnecting:
                    console.warn("[SignalRTransport] Reconnecting...");
                    this.setState(ConnectionState.RECONNECTING);
                    break;
                case states.disconnected:
                    this.setState(ConnectionState.DISCONNECTED);
                    break;
                default:
                    break;
            }
        });

        this.connection.reconnected(() => {
            console.log("[SignalRTransport] Reconnected successfully. ConnectionId:", this.connection.id);
            this.setState(ConnectionState.CONNECTED);
        });

        this.connection.disconnected(() => {
            console.warn("[SignalRTransport] Disconnected.");
            this.setState(ConnectionState.DISCONNECTED);
        });

        this.connection.error((error) => {
            console.warn("[SignalRTransport] Connection error:", error);
        });
    }

    bindPendingSubscriptions() {
        if (!this.hubProxy) return;

        for (const [eventName] of this.pendingSubscriptions.entries()) {
            if (!this.registeredHubEvents.has(eventName)) {
                this.registeredHubEvents.add(eventName);
                this.hubProxy.on(eventName, (...args) => {
                    const currentHandlers = this.pendingSubscriptions.get(eventName);
                    if (currentHandlers) {
                        currentHandlers.forEach(handler => {
                            try {
                                handler(...args);
                            } catch (e) {
                                console.error(`[SignalRTransport] Error handling event ${eventName}:`, e);
                            }
                        });
                    }
                });
            }
        }
    }

    async disconnect() {
        if (this.connection) {
            try {
                this.connection.stop();
            } catch (err) {
                console.error("[SignalRTransport] Error stopping connection:", err);
            } finally {
                this.connection = null;
                this.hubProxy = null;
                this.registeredHubEvents.clear();
                this.setState(ConnectionState.DISCONNECTED);
            }
        }
    }

    async invoke(methodName, ...args) {
        if (!this.hubProxy || !this.connection) {
            console.warn(`[SignalRTransport] Cannot invoke '${methodName}': connection not initialized.`);
            return null;
        }

        // If currently connecting, wait for connection attempt to finish
        if (this.connectPromise) {
            try {
                await this.connectPromise;
            } catch (e) {
                // ignore
            }
        }

        const isConnected = this.connection && this.connection.state === ($.signalR?.connectionState?.connected ?? 1);
        if (!isConnected) {
            console.warn(`[SignalRTransport] Cannot invoke '${methodName}': connection is not in connected state (state: ${this.connection ? this.connection.state : 'null'}).`);
            return null;
        }

        try {
            return await this.hubProxy.invoke(methodName, ...args);
        } catch (error) {
            console.warn(`[SignalRTransport] Error invoking '${methodName}':`, error && error.message ? error.message : error);
            return null;
        }
    }

    on(eventName, handler) {
        if (!this.pendingSubscriptions.has(eventName)) {
            this.pendingSubscriptions.set(eventName, new Set());
        }
        this.pendingSubscriptions.get(eventName).add(handler);

        if (this.hubProxy && !this.registeredHubEvents.has(eventName)) {
            this.registeredHubEvents.add(eventName);
            this.hubProxy.on(eventName, (...args) => {
                const currentHandlers = this.pendingSubscriptions.get(eventName);
                if (currentHandlers) {
                    currentHandlers.forEach(h => {
                        try {
                            h(...args);
                        } catch (e) {
                            console.error(`[SignalRTransport] Error handling event ${eventName}:`, e);
                        }
                    });
                }
            });
        }
    }

    off(eventName, handler) {
        if (this.pendingSubscriptions.has(eventName)) {
            const handlers = this.pendingSubscriptions.get(eventName);
            if (handler) {
                handlers.delete(handler);
                if (handlers.size === 0) {
                    this.pendingSubscriptions.delete(eventName);
                }
            } else {
                this.pendingSubscriptions.delete(eventName);
            }
        }

        if (this.hubProxy && !this.pendingSubscriptions.has(eventName)) {
            this.hubProxy.off(eventName);
            this.registeredHubEvents.delete(eventName);
        }
    }

    getState() {
        return this.state;
    }

    getConnectionId() {
        return this.connection ? this.connection.id : null;
    }

    setState(newState) {
        this.state = newState;
        this.stateChangeListeners.forEach(listener => {
            try {
                listener(newState);
            } catch (e) {
                console.error("[SignalRTransport] Error in stateChangeListener:", e);
            }
        });
    }

    onStateChange(listener) {
        this.stateChangeListeners.add(listener);
        return () => this.stateChangeListeners.delete(listener);
    }
}
